import React, { useMemo } from "react";

import {
  HABITAT_COLORS,
  type BoardSnapshot,
  type Habitat,
  type Tile,
  getActLabel,
} from "../vowGameLogic";
import { PANEL_CLASS } from "./uiConstants";

const HABITAT_NAMES: Record<Habitat, string> = {
  R: "Reedbed",
  P: "Meadow",
  E: "Open Water",
  B: "Woodland",
};

type HabitatSnapshotTimelineSectionProps = {
  snapshots: BoardSnapshot[];
};

const HabitatSnapshotTimelineSection: React.FC<
  HabitatSnapshotTimelineSectionProps
> = ({ snapshots }) => {
  const timelineEntries = useMemo(() => {
    return snapshots.map((snapshot, index) => ({
      key: `${snapshot.title}-${index}`,
      snapshot,
      actLabel: snapshot.annee ? getActLabel(snapshot.annee) : null,
    }));
  }, [snapshots]);

  if (timelineEntries.length === 0) {
    return null;
  }

  return (
    <section className={`${PANEL_CLASS} p-6`}>
      <h2 className="text-center text-2xl font-bold text-slate-900 md:text-left">
        Habitat Timeline
      </h2>
      <p className="mt-2 text-sm text-slate-700">
        Track how the habitat layout evolved at key decision points alongside
        the selected Water Management (WM) scenario.
      </p>

      <div className="mt-5 grid gap-4 md:grid-cols-2">
        {timelineEntries.map(({ key, snapshot, actLabel }) => (
          <article
            key={key}
            className="flex flex-col gap-3 border-2 border-black bg-[#dcd6c8] p-4 shadow-sm"
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  {snapshot.title}
                </h3>
                {actLabel && (
                  <p className="text-sm text-slate-700">{actLabel}</p>
                )}
              </div>
              <p className="text-sm font-semibold uppercase tracking-wide text-slate-800">
                WM {snapshot.wmChoice ?? "—"}
              </p>
            </div>
            {snapshot.includesActions && (
              <p className="text-xs font-semibold uppercase tracking-wide text-purple-700">
                Master view — includes planned actions
              </p>
            )}
            <MiniHabitatGrid plateau={snapshot.plateau} />
          </article>
        ))}
      </div>

      <HabitatLegend />
    </section>
  );
};

type MiniHabitatGridProps = {
  plateau: Tile[];
};

const MiniHabitatGrid: React.FC<MiniHabitatGridProps> = ({ plateau }) => (
  <div className="mt-2 grid grid-cols-4 gap-1">
    {plateau.map((tile) => (
      <span
        key={tile.id}
        className="block h-8 w-8 rounded-sm border border-black/20"
        style={{ backgroundColor: HABITAT_COLORS[tile.habitat as Habitat] }}
        title={`${tile.id}: ${HABITAT_NAMES[tile.habitat as Habitat]}`}
      />
    ))}
  </div>
);

const HabitatLegend: React.FC = () => (
  <div className="mt-6 flex flex-wrap items-center gap-3 text-xs text-slate-700">
    {(Object.entries(HABITAT_NAMES) as [Habitat, string][]).map(
      ([habitat, name]) => (
        <span key={habitat} className="flex items-center gap-2">
          <span
            className="h-3 w-3 rounded-sm border border-black/20"
            style={{ backgroundColor: HABITAT_COLORS[habitat] }}
          />
          <span>{name}</span>
        </span>
      )
    )}
  </div>
);

export default HabitatSnapshotTimelineSection;
